import { FC } from 'react';
import { Invoice } from 'types';

const DollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

export const InvoiceSummaryTab: FC<{ invoice: Invoice | null }> = ({ invoice }) => {
  return (
    <>
      <UnitsByType invoice={invoice} />
      <EmptyUnitsList invoice={invoice} />
      <LoadedUnitsList invoice={invoice} />
      <AllUnitsTotalList invoice={invoice} />
    </>
  );
};

const getTotalUnitsCredit = (invoice: Invoice, loaded: boolean) => {
  if (!invoice?.items) {
    return 0;
  }
  // Sum up unit credits of (un)loaded Units
  // since unitCredits is only a map of Unit IDs, we need to reference against InvoiceItems
  return Object.keys(invoice.unitCredits).reduce((sum, unitId) => {
    return (
      sum + (invoice.items.some(i => i.unitId === +unitId && i.loaded === loaded) ? invoice.unitCredits[unitId] : 0)
    );
  }, 0);
};

const UnitsByType: FC<{ invoice: Invoice | null }> = ({ invoice }) => {
  const gridTemplateColumns = '.75fr 1fr 1.25fr 1.6fr .6fr 1fr .6fr 1fr 1fr 1fr .6fr 1fr 1fr';
  return (
    <div className="card-primary raised">
      <div className="card-header">
        <h4 className="">ALL UNITS EMPTY & LOADED</h4>
      </div>
      <ul className="list-group">
        {/* Header Row */}
        <li className="list-group-item list-group-header grid-13" style={{ gridTemplateColumns }}>
          <p className="pl-3">Size</p>
          <p>Total Units</p>
          <p>Charged Days</p>
          <p>Storage Charge</p>
          <p>In</p>
          <p>Gate In</p>
          <p>Out</p>
          <p>Gate Out</p>
          <p>Off</p>
          <p>Lift Off</p>
          <p>On</p>
          <p>Lift On</p>
          <p>Credit</p>
        </li>
        {/* Map grouped invoice items */}
        {invoice &&
          Object.entries(invoice.getItemsGroupedBySizeType()).map(([sizeType, invoiceItems]) => {
            return (
              <li key={sizeType} className="list-group-item grid-12 text-base" style={{ gridTemplateColumns }}>
                <p className="pl-3">{sizeType}</p>
                <p>{invoiceItems.items.length}</p>
                <p>{invoiceItems.items.reduce((sum, i) => (sum += i.chargedDays), 0)}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.storageCharge), 0))}</p>
                <p>{invoiceItems.items.filter(i => invoice.isWithinBillingPeriod(i.gateInDate)).length}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.gateInCharge), 0))}</p>
                <p>{invoiceItems.items.filter(i => invoice.isWithinBillingPeriod(i.gateOutDate)).length}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.gateOutCharge), 0))}</p>
                <p>{invoiceItems.items.reduce((sum, i) => (sum += i.liftOffCount), 0)}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.liftOffCharge), 0))}</p>
                <p>{invoiceItems.items.reduce((sum, i) => (sum += i.liftOnCount), 0)}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.liftOnCharge), 0))}</p>
                <p>{DollarFormatter.format(invoiceItems.credit)}</p>
              </li>
            );
          })}
        {/* Totals */}
        {invoice && (
          <li className="list-group-item list-group-footer grid-12" style={{ gridTemplateColumns }}>
            <p className="pl-3 font-weight-bold">TOTAL</p>
            <p>{invoice.items.length}</p>
            <p>{invoice.items.reduce((sum, i) => (sum += i.chargedDays), 0)}</p>
            <p>{DollarFormatter.format(invoice.items.reduce((sum, i) => (sum += i.storageCharge), 0))}</p>
            <p>{invoice.items.filter(i => invoice.isWithinBillingPeriod(i.gateInDate)).length}</p>
            <p>{DollarFormatter.format(invoice.items.reduce((sum, i) => (sum += i.gateInCharge), 0))}</p>
            <p>{invoice.items.filter(i => invoice.isWithinBillingPeriod(i.gateOutDate)).length}</p>
            <p>{DollarFormatter.format(invoice.items.reduce((sum, i) => (sum += i.gateOutCharge), 0))}</p>
            <p>{invoice.items.reduce((sum, i) => (sum += i.liftOffCount), 0)}</p>
            <p>{DollarFormatter.format(invoice.items.reduce((sum, i) => (sum += i.liftOffCharge), 0))}</p>
            <p>{invoice.items.reduce((sum, i) => (sum += i.liftOnCount), 0)}</p>
            <p>{DollarFormatter.format(invoice.items.reduce((sum, i) => (sum += i.liftOnCharge), 0))}</p>
            <p>{DollarFormatter.format(Object.values(invoice.unitCredits).reduce((sum, i) => (sum += i), 0))}</p>
          </li>
        )}
      </ul>
    </div>
  );
};

const EmptyUnitsList: FC<{ invoice: Invoice | null }> = ({ invoice }) => {
  const gridTemplateColumns = '1fr 1.3fr .5fr 1fr .5fr 1fr .5fr 1fr .5fr 1fr 1fr';
  return (
    <div className="card-primary raised">
      <div className="card-header">
        <h4 className="">EMPTY UNITS</h4>
      </div>
      <ul className="list-group">
        {/* Header Row */}
        <li className="list-group-item list-group-header grid-13" style={{ gridTemplateColumns }}>
          <p className="pl-3">Type</p>
          <p>Storage Charge</p>
          <p>In</p>
          <p>Gate In</p>
          <p>Out</p>
          <p>Gate Out</p>
          <p>Off</p>
          <p>Lift Off</p>
          <p>On</p>
          <p>Lift On</p>
          <p>Credit</p>
        </li>
        {/* Map grouped invoice items */}
        {invoice &&
          Object.entries(invoice.getUnloadedItemsGroupedByUnitType()).map(([unitType, invoiceItems]) => {
            return (
              <li key={unitType} className="list-group-item grid-10 text-base" style={{ gridTemplateColumns }}>
                <p className="pl-3">{unitType}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.storageCharge), 0))}</p>
                <p>{invoiceItems.items.filter(i => invoice.isWithinBillingPeriod(i.gateInDate)).length}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.gateInCharge), 0))}</p>
                <p>{invoiceItems.items.filter(i => invoice.isWithinBillingPeriod(i.gateOutDate)).length}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.gateOutCharge), 0))}</p>
                <p>{invoiceItems.items.reduce((sum, i) => (sum += i.liftOffCount), 0)}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.liftOffCharge), 0))}</p>
                <p>{invoiceItems.items.reduce((sum, i) => (sum += i.liftOnCount), 0)}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.liftOnCharge), 0))}</p>
                <p>{DollarFormatter.format(invoiceItems.credit)}</p>
              </li>
            );
          })}
        {/* Totals */}
        {invoice && (
          <li className="list-group-item list-group-footer grid-12" style={{ gridTemplateColumns }}>
            <p className="pl-3 font-weight-bold">TOTAL</p>
            <p>
              {DollarFormatter.format(
                invoice.items.filter(i => !i.loaded).reduce((sum, i) => (sum += i.storageCharge), 0)
              )}
            </p>
            <p>
              {invoice.items.filter(i => !i.loaded).filter(i => invoice.isWithinBillingPeriod(i.gateInDate)).length}
            </p>
            <p>
              {DollarFormatter.format(
                invoice.items.filter(i => !i.loaded).reduce((sum, i) => (sum += i.gateInCharge), 0)
              )}
            </p>
            <p>
              {invoice.items.filter(i => !i.loaded).filter(i => invoice.isWithinBillingPeriod(i.gateOutDate)).length}
            </p>
            <p>
              {DollarFormatter.format(
                invoice.items.filter(i => !i.loaded).reduce((sum, i) => (sum += i.gateOutCharge), 0)
              )}
            </p>
            <p>{invoice.items.filter(i => !i.loaded).reduce((sum, i) => (sum += i.liftOffCount), 0)}</p>
            <p>
              {DollarFormatter.format(
                invoice.items.filter(i => !i.loaded).reduce((sum, i) => (sum += i.liftOffCharge), 0)
              )}
            </p>
            <p>{invoice.items.filter(i => !i.loaded).reduce((sum, i) => (sum += i.liftOnCount), 0)}</p>
            <p>
              {DollarFormatter.format(
                invoice.items.filter(i => !i.loaded).reduce((sum, i) => (sum += i.liftOnCharge), 0)
              )}
            </p>
            <p>{DollarFormatter.format(getTotalUnitsCredit(invoice, false))}</p>
          </li>
        )}
      </ul>
    </div>
  );
};

const LoadedUnitsList: FC<{ invoice: Invoice | null }> = ({ invoice }) => {
  const gridTemplateColumns = '1fr 1.3fr .5fr 1fr .5fr 1fr .5fr 1fr .5fr 1fr 1fr';
  return (
    <div className="card-primary raised">
      <div className="card-header">
        <h4 className="">LOADED UNITS</h4>
      </div>
      <ul className="list-group">
        {/* Header Row */}
        <li className="list-group-item list-group-header grid-12" style={{ gridTemplateColumns }}>
          <p className="pl-3">Type</p>
          <p>Storage Charge</p>
          <p>In</p>
          <p>Gate In</p>
          <p>Out</p>
          <p>Gate Out</p>
          <p>Off</p>
          <p>Lift Off</p>
          <p>On</p>
          <p>Lift On</p>
          <p>Credit</p>
        </li>
        {invoice &&
          Object.entries(invoice.getLoadedItemsGroupedByUnitType()).map(([unitType, invoiceItems]) => {
            return (
              <li key={unitType} className="list-group-item grid-10 text-base" style={{ gridTemplateColumns }}>
                <p className="pl-3">{unitType}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.storageCharge), 0))}</p>
                <p>{invoiceItems.items.filter(i => invoice.isWithinBillingPeriod(i.gateInDate)).length}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.gateInCharge), 0))}</p>
                <p>{invoiceItems.items.filter(i => invoice.isWithinBillingPeriod(i.gateOutDate)).length}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.gateOutCharge), 0))}</p>
                <p>{invoiceItems.items.reduce((sum, i) => (sum += i.liftOffCount), 0)}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.liftOffCharge), 0))}</p>
                <p>{invoiceItems.items.reduce((sum, i) => (sum += i.liftOnCount), 0)}</p>
                <p>{DollarFormatter.format(invoiceItems.items.reduce((sum, i) => (sum += i.liftOnCharge), 0))}</p>
                <p>{DollarFormatter.format(invoiceItems.credit)}</p>
              </li>
            );
          })}
        {/* Totals */}
        {invoice && (
          <li className="list-group-item list-group-footer grid-12" style={{ gridTemplateColumns }}>
            <p className="pl-3 font-weight-bold">TOTAL</p>
            <p>
              {DollarFormatter.format(
                invoice.items.filter(i => i.loaded).reduce((sum, i) => (sum += i.storageCharge), 0)
              )}
            </p>
            <p>{invoice.items.filter(i => i.loaded).filter(i => invoice.isWithinBillingPeriod(i.gateInDate)).length}</p>
            <p>
              {DollarFormatter.format(
                invoice.items.filter(i => i.loaded).reduce((sum, i) => (sum += i.gateInCharge), 0)
              )}
            </p>
            <p>
              {invoice.items.filter(i => i.loaded).filter(i => invoice.isWithinBillingPeriod(i.gateOutDate)).length}
            </p>
            <p>
              {DollarFormatter.format(
                invoice.items.filter(i => i.loaded).reduce((sum, i) => (sum += i.gateOutCharge), 0)
              )}
            </p>
            <p>{invoice.items.filter(i => i.loaded).reduce((sum, i) => (sum += i.liftOffCount), 0)}</p>
            <p>
              {DollarFormatter.format(
                invoice.items.filter(i => i.loaded).reduce((sum, i) => (sum += i.liftOffCharge), 0)
              )}
            </p>
            <p>{invoice.items.filter(i => i.loaded).reduce((sum, i) => (sum += i.liftOnCount), 0)}</p>
            <p>
              {DollarFormatter.format(
                invoice.items.filter(i => i.loaded).reduce((sum, i) => (sum += i.liftOnCharge), 0)
              )}
            </p>
            <p>{DollarFormatter.format(getTotalUnitsCredit(invoice, true))}</p>
          </li>
        )}
      </ul>
    </div>
  );
};

const AllUnitsTotalList: FC<{ invoice: Invoice | null }> = ({ invoice }) => {
  const gridTemplateColumns = '1.3fr .5fr 1fr .5fr 1fr .5fr 1fr .5fr 1fr 1fr';

  return (
    <div className="card-primary raised">
      <div className="card-header">
        <h4 className="pl-3">INVOICE TOTALS</h4>
      </div>
      <ul className="list-group">
        {/* Header Row */}
        <li className="list-group-item list-group-header grid-9" style={{ gridTemplateColumns }}>
          <p className="pl-3">Daily Storage</p>
          <p>In</p>
          <p>Gate In</p>
          <p>Out</p>
          <p>Gate Out</p>
          <p>Off</p>
          <p>Lift Off</p>
          <p>On</p>
          <p>Lift On</p>
          <p>Credit</p>
        </li>
        {invoice && (
          <li className="list-group-item  grid-12 text-base" style={{ gridTemplateColumns }}>
            <p className="pl-3">
              {DollarFormatter.format(invoice.items.reduce((sum, i) => (sum += i.storageCharge), 0))}
            </p>
            <p>{invoice.items.filter(i => invoice.isWithinBillingPeriod(i.gateInDate)).length}</p>
            <p>{DollarFormatter.format(invoice.items.reduce((sum, i) => (sum += i.gateInCharge), 0))}</p>
            <p>{invoice.items.filter(i => invoice.isWithinBillingPeriod(i.gateOutDate)).length}</p>
            <p>{DollarFormatter.format(invoice.items.reduce((sum, i) => (sum += i.gateOutCharge), 0))}</p>
            <p>{invoice.items.reduce((sum, i) => (sum += i.liftOffCount), 0)}</p>
            <p>{DollarFormatter.format(invoice.items.reduce((sum, i) => (sum += i.liftOffCharge), 0))}</p>
            <p>{invoice.items.reduce((sum, i) => (sum += i.liftOnCount), 0)}</p>
            <p>{DollarFormatter.format(invoice.items.reduce((sum, i) => (sum += i.liftOnCharge), 0))}</p>
            <p>{DollarFormatter.format(Object.values(invoice.unitCredits).reduce((sum, i) => (sum += i), 0))}</p>
          </li>
        )}
        <li className="list-group-item list-group-footer grid-9" style={{ gridTemplateColumns }}>
          <p className="pl-3 font-weight-bold">TOTAL</p>
          <p className="font-weight-bold col-start-9">
            {DollarFormatter.format(
              (invoice?.items.reduce((sum, item) => (sum += item.getAllCharges()), 0) ?? 0) -
                (invoice?.unitCredits ? Object.values(invoice.unitCredits).reduce((sum, i) => (sum += i), 0) : 0)
            )}
          </p>
        </li>
      </ul>
    </div>
  );
};
